import type { BufferGeometry } from 'three';
import { hexToLinear, type LinearRGB } from '../../core/color';
import type { Vec3 } from '../../geo/types';
import { createMeshBuilder, type MeshBuilder, type VertexStyle } from '../mesh/meshBuilder';
import { SURFACE } from '../mesh/attributes';
import { hullPoint, tierDeckAt, type FormedTemplate } from './shipForm';
import { sparSolid } from './shipSolids';
import { timberPost } from './shipTimber';

/*
 * The lantern a lit ship shows after dark (docs/NIGHT.md): the hand lantern of the streets
 * (life/props.ts) at nearly three times its size, a tin frame with panes of scraped horn and a
 * cone over the candle, on a staff in the middle of the stern rail of the highest castle deck.
 * Neither the painting nor the woodcut draws a lantern on a ship: that one stands there, its form
 * and every size here are estimates.
 */
export const SHIP_LANTERN = {
  /** The staff stands this far inside the stern, and carries the horn's foot this high above the deck. */
  inboard: 0.3,
  foot: 2.5,
  staff: 0.09,
  /** The horn: half its width and its height. */
  half: 0.16,
  height: 0.42,
  bar: 0.035,
  plate: 0.05,
  eaves: 0.22,
  roof: 0.2,
  crown: 0.03,
  ring: 0.09,
} as const;

const TIN = hexToLinear('#4b463b');
const HORN = hexToLinear('#d9bc7c');

/* Where the staff stands on the deck, in the ship's frame. */
function footOf(template: FormedTemplate): Vec3 {
  const poop = template.form.aftcastle.at(-1)!;
  return [hullPoint(template, 1, 1)[0] - SHIP_LANTERN.inboard, tierDeckAt(template, poop, 1), 0];
}

/** Where the candle burns, in the ship's frame: the middle of the horn. */
export function lanternFlame(template: FormedTemplate): Vec3 {
  const [x, deck, z] = footOf(template);
  return [x, deck + SHIP_LANTERN.foot + SHIP_LANTERN.height / 2, z];
}

/* A square of half-width `half` at the foot and `halfTop` at the head, upright from `y0` to `y1`:
   a spar of four sides stands its corners on the diagonals. */
function square(
  builder: MeshBuilder,
  style: VertexStyle,
  at: Vec3,
  [y0, y1]: readonly [number, number],
  half: number,
  halfTop = half
): void {
  sparSolid(builder, style, {
    from: [at[0], y0, at[2]],
    to: [at[0], y1, at[2]],
    radiusFrom: half * Math.SQRT2,
    radiusTo: halfTop * Math.SQRT2,
    sides: 4,
    painterWidthScale: 1,
  });
}

/** A ship's lantern on its staff, in the ship's frame. */
export function buildShipLantern(
  template: FormedTemplate,
  wood: LinearRGB,
  edgeId: number,
  seed: number
): BufferGeometry {
  const builder = createMeshBuilder(256);
  const style = (color: LinearRGB): VertexStyle => ({
    color,
    surface: SURFACE.wood,
    edgeId,
    seed,
    strokeAngle: 0,
    extent: [0, 0],
  });
  const { foot, staff, half, height, bar, plate, eaves, roof, crown, ring } = SHIP_LANTERN;
  const base = footOf(template);
  const low = base[1] + foot;
  const high = low + height;
  timberPost(builder, style(wood), base, foot - plate, staff);
  square(builder, style(TIN), base, [low - plate, low], half + bar);
  square(builder, style(HORN), base, [low, high], half);
  for (const dx of [-1, 1])
    for (const dz of [-1, 1])
      timberPost(builder, style(TIN), [base[0] + dx * half, low, base[2] + dz * half], height, bar);
  square(builder, style(TIN), base, [high, high + roof], eaves, crown);
  square(builder, style(TIN), base, [high + roof, high + roof + ring], crown / 2);
  return builder.build();
}
